import React from 'react';
import { useMetaTags } from '../hooks/useMetaTags';
export function CookiePolicyPage() {
  useMetaTags({
    title: 'Cookie Policy | FindOnlineDegrees',
    description: 'Learn about how FindOnlineDegrees uses cookies and similar technologies.'
  });
  return <div className="min-h-screen bg-gray-100" data-id="element-858">
      {/* Hero Section */}
      <section className="w-full bg-[#1a2332] text-white py-16 px-4" data-id="element-859">
        <div className="max-w-4xl mx-auto text-center" data-id="element-860">
          <h1 className="text-3xl md:text-5xl font-bold mb-4" data-id="element-861">Cookie Notice</h1>
          <p className="text-gray-300 text-sm" data-id="element-862">Last updated: January 2026</p>
        </div>
      </section>

      {/* Content Section */}
      <section className="max-w-4xl mx-auto px-4 py-12" data-id="element-863">
        <div className="bg-white rounded-lg shadow-sm p-8 md:p-12 space-y-8" data-id="element-864">
          <div className="space-y-4 text-gray-700 leading-relaxed" data-id="element-865">
            <p data-id="element-866">
              This Cookie Notice explains how FindOnlineDegrees.org uses cookies
              and similar technologies to recognize you when you visit our
              website. It explains what these technologies are, why we use them,
              and your rights to control our use of them.
            </p>
          </div>

          {/* What Are Cookies */}
          <div data-id="element-867">
            <h2 className="text-2xl font-bold text-[#3b82f6] mb-4" data-id="element-868">
              1. WHAT ARE COOKIES?
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed" data-id="element-869">
              <p data-id="element-870">
                Cookies are small data files placed on your device when you
                visit a website. Cookies are widely used to make websites work
                more efficiently and to provide reporting information.
              </p>
              <p data-id="element-871">
                Cookies set by the website owner (in this case,
                FindOnlineDegrees.org) are called "first-party cookies." Cookies
                set by parties other than the website owner are called
                "third-party cookies." Third-party cookies enable features or
                functionality such as advertising, analytics, and interactive
                content.
              </p>
            </div>
          </div>

          {/* Why Do We Use Cookies */}
          <div data-id="element-872">
            <h2 className="text-2xl font-bold text-[#3b82f6] mb-4" data-id="element-873">
              2. WHY DO WE USE COOKIES?
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed" data-id="element-874">
              <p data-id="element-875">
                We use first-party and third-party cookies for several reasons:
              </p>
              <ul className="list-disc pl-6 space-y-2" data-id="element-876">
                <li data-id="element-877">
                  <strong data-id="element-878">Essential cookies:</strong> Required for technical
                  operation of our website (e.g., account login, security).
                </li>
                <li data-id="element-879">
                  <strong data-id="element-880">Performance and analytics cookies:</strong> Collect
                  information to help us understand website performance and
                  improve user experience.
                </li>
                <li data-id="element-881">
                  <strong data-id="element-882">Advertising cookies:</strong> Deliver relevant ads and
                  measure effectiveness of our marketing campaigns.
                </li>
                <li data-id="element-883">
                  <strong data-id="element-884">Functionality cookies:</strong> Remember your
                  preferences and settings.
                </li>
              </ul>
            </div>
          </div>

          {/* Types of Cookies */}
          <div data-id="element-885">
            <h2 className="text-2xl font-bold text-[#3b82f6] mb-4" data-id="element-886">
              3. TYPES OF COOKIES WE USE
            </h2>
            <div className="space-y-6 text-gray-700 leading-relaxed" data-id="element-887">
              <div data-id="element-888">
                <h3 className="font-bold text-lg mb-2" data-id="element-889">
                  Strictly Necessary Cookies
                </h3>
                <p data-id="element-890">
                  These are essential to provide services and cannot be switched
                  off. They are usually only set in response to actions you
                  take, such as setting privacy preferences or filling in forms.
                </p>
              </div>

              <div data-id="element-891">
                <h3 className="font-bold text-lg mb-2" data-id="element-892">
                  Analytics and Performance Cookies
                </h3>
                <p data-id="element-893">
                  Help us count visits, traffic sources, and measure site
                  performance. Examples include Google Analytics cookies. These
                  cookies allow us to know which pages are most and least
                  popular and see how visitors move around the site.
                </p>
              </div>

              <div data-id="element-894">
                <h3 className="font-bold text-lg mb-2" data-id="element-895">Advertising Cookies</h3>
                <p data-id="element-896">
                  Used by us and third parties (such as Google Ads, Bing Ads, or
                  other ad networks) to build a profile of your interests and
                  show you relevant ads on other sites. They work by uniquely
                  identifying your browser and device.
                </p>
              </div>

              <div data-id="element-897">
                <h3 className="font-bold text-lg mb-2" data-id="element-898">
                  Functionality Cookies
                </h3>
                <p data-id="element-899">
                  Enable enhanced functionality and personalization, such as
                  remembering your preferences. They may be set by us or by
                  third-party providers whose services we use on our pages.
                </p>
              </div>
            </div>
          </div>

          {/* How to Control Cookies */}
          <div data-id="element-900">
            <h2 className="text-2xl font-bold text-[#3b82f6] mb-4" data-id="element-901">
              4. HOW CAN YOU CONTROL COOKIES?
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed" data-id="element-902">
              <p data-id="element-903">
                You have the right to decide whether to accept or reject
                cookies. You can exercise your cookie preferences through your
                browser settings:
              </p>
              <ul className="list-disc pl-6 space-y-2" data-id="element-904">
                <li data-id="element-905">
                  <strong data-id="element-906">Browser Controls:</strong> Most web browsers allow you
                  to control cookies through their settings. However, if you
                  limit the ability of websites to set cookies, you may worsen
                  your overall user experience.
                </li>
                <li data-id="element-907">
                  <strong data-id="element-908">Opt-Out Links:</strong> You can opt out of
                  interest-based advertising through industry opt-out pages such
                  as the Digital Advertising Alliance (DAA) or Network
                  Advertising Initiative (NAI).
                </li>
                <li data-id="element-909">
                  <strong data-id="element-910">Do Not Track:</strong> Some browsers have a "Do Not
                  Track" feature that lets you tell websites you do not want to
                  have your online activities tracked.
                </li>
              </ul>
            </div>
          </div>

          {/* Third-Party Cookies */}
          <div data-id="element-911">
            <h2 className="text-2xl font-bold text-[#3b82f6] mb-4" data-id="element-912">
              5. THIRD-PARTY COOKIES
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed" data-id="element-913">
              <p data-id="element-914">
                In addition to our own cookies, we may use various third-party
                cookies to report usage statistics, deliver advertisements, and
                so on. Common third-party services include:
              </p>
              <ul className="list-disc pl-6 space-y-2" data-id="element-915">
                <li data-id="element-916">Google Analytics (analytics and performance)</li>
                <li data-id="element-917">Google Ads (advertising)</li>
                <li data-id="element-918">Bing Ads (advertising)</li>
                <li data-id="element-919">Facebook Pixel (advertising and analytics)</li>
              </ul>
            </div>
          </div>

          {/* Updates to This Notice */}
          <div data-id="element-920">
            <h2 className="text-2xl font-bold text-[#3b82f6] mb-4" data-id="element-921">
              6. UPDATES TO THIS COOKIE NOTICE
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed" data-id="element-922">
              <p data-id="element-923">
                We may update this Cookie Notice from time to time to reflect
                changes in the cookies we use or for other operational, legal,
                or regulatory reasons. Please revisit this Cookie Notice
                regularly to stay informed about our use of cookies.
              </p>
            </div>
          </div>

          {/* Contact Us */}
          <div data-id="element-924">
            <h2 className="text-2xl font-bold text-[#3b82f6] mb-4" data-id="element-925">
              7. CONTACT US
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed" data-id="element-926">
              <p data-id="element-927">
                If you have questions about our use of cookies or this Cookie
                Notice, please contact us through the information provided on
                our website.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>;
}